import type { SituacaoPedido } from '@arena/shared/dominio';
import type {
  AlteracaoDePromocao,
  ConfiguracaoDoFormulario,
  ConfirmacaoDePedido,
  DetalheDoPedido,
  FormularioDaArena,
  FormularioPublico,
  ListaDePedidos,
  NovaPromocao,
  OrigemDosAlunos,
  PedidoDeProjecao,
  PedidoDeReserva,
  PedidoNaFila,
  ProjecaoDePromocao,
  PromocaoCadastrada,
  RecusaDePedido,
} from '@arena/shared/schemas';
import { api } from './cliente.js';

/**
 * As chamadas de reservas pedidas — `#v-site`, `#m-pedido`, `#m-promo` e o
 * card "Pedidos do site" de `#v-agenda`.
 *
 * Não há mais porta pública: o formulário embutível do documento 14 foi
 * removido, e o pedido entra por `POST /site/pedidos`, com a sessão da
 * recepção.
 */
export const siteApi = {
  pedidos(opcoes: { situacao?: SituacaoPedido; cursor?: string; limite?: number } = {}) {
    const query = new URLSearchParams();
    if (opcoes.situacao) query.set('situacao', opcoes.situacao);
    if (opcoes.cursor) query.set('cursor', opcoes.cursor);
    if (opcoes.limite) query.set('limite', String(opcoes.limite));
    const texto = query.toString();
    return api<ListaDePedidos>(`/site/pedidos${texto ? `?${texto}` : ''}`);
  },

  pedido(id: string): Promise<DetalheDoPedido> {
    return api<DetalheDoPedido>(`/site/pedidos/${id}`);
  },

  /** O pedido que a recepção anota — "ligou pedindo sábado às 20h". */
  registrar(dados: PedidoDeReserva): Promise<DetalheDoPedido> {
    return api<DetalheDoPedido>('/site/pedidos', { metodo: 'POST', corpo: dados });
  },

  confirmar(id: string, dados: ConfirmacaoDePedido): Promise<PedidoNaFila> {
    return api<PedidoNaFila>(`/site/pedidos/${id}/confirmar`, { metodo: 'POST', corpo: dados });
  },

  recusar(id: string, dados: RecusaDePedido): Promise<PedidoNaFila> {
    return api<PedidoNaFila>(`/site/pedidos/${id}/recusar`, { metodo: 'POST', corpo: dados });
  },

  formulario(): Promise<FormularioDaArena> {
    return api<FormularioDaArena>('/site/formulario');
  },

  salvarFormulario(dados: ConfiguracaoDoFormulario): Promise<FormularioDaArena> {
    return api<FormularioDaArena>('/site/formulario', { metodo: 'PUT', corpo: dados });
  },

  previa(): Promise<FormularioPublico> {
    return api<FormularioPublico>('/site/formulario/previa');
  },

  promocoes(): Promise<PromocaoCadastrada[]> {
    return api<PromocaoCadastrada[]>('/site/promocoes');
  },

  criarPromocao(dados: NovaPromocao): Promise<PromocaoCadastrada> {
    return api<PromocaoCadastrada>('/site/promocoes', { metodo: 'POST', corpo: dados });
  },

  alterarPromocao(id: string, dados: AlteracaoDePromocao): Promise<PromocaoCadastrada> {
    return api<PromocaoCadastrada>(`/site/promocoes/${id}`, { metodo: 'PATCH', corpo: dados });
  },

  projetarPromocao(dados: PedidoDeProjecao): Promise<ProjecaoDePromocao> {
    return api<ProjecaoDePromocao>('/site/promocoes/projecao', { metodo: 'POST', corpo: dados });
  },

  origemDosAlunos(): Promise<OrigemDosAlunos> {
    return api<OrigemDosAlunos>('/site/origem-dos-alunos');
  },
};
